'use client';

import { type RefObject, useEffect, useRef, useState } from 'react';

import { useAudioMonitoring } from '@/features/audio-monitoring';
import { useSpeechRecognition } from '@/features/speech-recognition';
import type { ChatComposerVoice } from '@/shared/ui/chat';

import type { ControlsTrack } from '../lib/controls-analytics';

export function useComposerVoice({
	value,
	onChange,
	disabled,
	fieldRef,
	track,
}: {
	value: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	fieldRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
	track?: ControlsTrack;
}): ChatComposerVoice {
	const [isRecording, setIsRecording] = useState(false);

	// true, пока запись запущена удержанием пробела — отпускание остановит её.
	// Клик по микрофону этот флаг не выставляет, поэтому пробел не закроет
	// «обычную» (кликнутую) запись случайным касанием.
	const pttActiveRef = useRef(false);
	// Таймер «долгого удержания»: запись стартует только если пробел удерживается
	// дольше PTT_HOLD_MS. Короткий тап ничего не делает.
	const pttHoldTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isRecordingRef = useRef(false);
	isRecordingRef.current = isRecording;

	const PTT_HOLD_MS = 500;

	const active = isRecording;
	const { bandLevels, micError } = useAudioMonitoring(active, null);

	// Текущее value через ref — колбэк распознавания не обязан ре-подписываться
	// на каждое нажатие клавиши, читаем актуальное значение в момент финального
	// сегмента речи.
	const valueRef = useRef(value);
	valueRef.current = value;

	useSpeechRecognition({
		active: isRecording,
		onFinal: (text) => {
			const current = valueRef.current;
			const sep = current && !/\s$/.test(current) ? ' ' : '';
			onChange(current + sep + text);
		},
	});

	// Форма заблокирована (AI печатает / интервью не начато) — глушим микрофон:
	// останавливаем активную запись.
	useEffect(() => {
		if (!disabled) return;
		pttActiveRef.current = false;
		setIsRecording(false);
	}, [disabled]);

	// Push-to-talk: удержание пробела стартует запись, отпускание — останавливает.
	// Пробел игнорируется, если фокус в другом текстовом инпуте, или в нашем
	// textarea уже что-то набрано (тогда пробел работает как обычный ввод).
	useEffect(() => {
		if (disabled) return;

		const shouldSkip = () => {
			const a = document.activeElement as HTMLElement | null;
			if (!a) return false;
			if (a === fieldRef.current) {
				return fieldRef.current.value.length > 0;
			}
			if (a.closest('button,a,[role="listbox"]')) return true;
			const tag = a.tagName;
			if (tag === 'INPUT' || tag === 'TEXTAREA' || a.isContentEditable) {
				return true;
			}
			return false;
		};

		const clearHoldTimer = () => {
			if (pttHoldTimerRef.current) {
				clearTimeout(pttHoldTimerRef.current);
				pttHoldTimerRef.current = null;
			}
		};

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.code !== 'Space' || e.repeat) return;
			if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
			if (shouldSkip()) return;
			// Запись уже идёт (кликнули по микрофону) — не «угоняем» её через PTT.
			if (isRecordingRef.current) return;
			// Уже стоит pending-таймер — повторно не запускаем.
			if (pttHoldTimerRef.current) return;
			e.preventDefault();
			pttHoldTimerRef.current = setTimeout(() => {
				pttHoldTimerRef.current = null;
				pttActiveRef.current = true;
				setIsRecording(true);
			}, PTT_HOLD_MS);
		};

		const onKeyUp = (e: KeyboardEvent) => {
			if (e.code !== 'Space') return;
			// Отпустили до того, как сработал hold-таймер — короткий тап, ничего не делаем.
			if (pttHoldTimerRef.current) {
				e.preventDefault();
				clearHoldTimer();
				return;
			}
			if (!pttActiveRef.current) return;
			e.preventDefault();
			pttActiveRef.current = false;
			setIsRecording(false);
		};

		// Если фокус ушёл со страницы при удержании пробела (alt-tab) —
		// сбрасываем PTT и pending-таймер, чтобы запись не «зависла».
		const onBlur = () => {
			clearHoldTimer();
			if (!pttActiveRef.current) return;
			pttActiveRef.current = false;
			setIsRecording(false);
		};

		document.addEventListener('keydown', onKeyDown);
		document.addEventListener('keyup', onKeyUp);
		window.addEventListener('blur', onBlur);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			document.removeEventListener('keyup', onKeyUp);
			window.removeEventListener('blur', onBlur);
			clearHoldTimer();
		};
	}, [disabled, fieldRef]);

	const lastRecording = useRef(false);
	useEffect(() => {
		if (isRecording !== lastRecording.current) {
			lastRecording.current = isRecording;
			track?.('voice', {
				action: isRecording ? 'start' : 'stop',
				stage: isRecording ? 'intent' : 'success',
			});
		}
	}, [isRecording, track]);
	const lastMicError = useRef<string | null>(null);
	useEffect(() => {
		if (micError && micError !== lastMicError.current)
			track?.('voice', { stage: 'error', reason: 'microphone' });
		lastMicError.current = micError;
	}, [micError, track]);

	return {
		recording: isRecording,
		levels: bandLevels,
		disabled,
		onToggle() {
			if (!disabled) setIsRecording((value) => !value);
		},
	};
}
